import React from 'react';
import { ExternalLink } from 'lucide-react';

type PlanningDocument = {
  thumbnail_url?: string | null;
  doc_link?: string | null;
  pdf_url?: string | null;
  document_url?: string | null;
  source_desc?: string | null;
  source_type?: string | null;
  role?: string | null;
  title?: string | null;
  page_number?: number | string | null;
};

type PlanningApplication = {
  reference_no?: string | null;
  address?: string | null;
  documents?: PlanningDocument[] | null;
};

export type PlanningArticlePhotoItem = {
  document: PlanningDocument;
  index: number;
  href: string;
  title: string;
  caption: string;
  alt: string;
  thumbnailUrl: string;
};

function clean(value: unknown) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function normalize(value: unknown) {
  return clean(value).toLowerCase();
}

function getDocumentHref(document: PlanningDocument) {
  return clean(document.doc_link || document.pdf_url || document.document_url || document.thumbnail_url);
}

function getDocumentTitle(document: PlanningDocument, index: number) {
  return (
    clean(document.source_desc) ||
    clean(document.title) ||
    clean(document.role) ||
    `Planning document preview ${index + 1}`
  );
}

function isPanApplication(application?: PlanningApplication | null) {
  return normalize(application?.reference_no).includes('/pan');
}

function isLocationPlan(document: PlanningDocument) {
  const searchable = [
    document.source_type,
    document.source_desc,
    document.title,
    document.role,
  ]
    .map(normalize)
    .join(' ');

  return searchable.includes('location plan') || searchable.includes('location_plan');
}

function buildCaption(document: PlanningDocument, application: PlanningApplication, index: number) {
  const reference = clean(application.reference_no);
  const page = clean(document.page_number);
  const title = getDocumentTitle(document, index);
  const label = isLocationPlan(document) ? 'Location plan' : title;
  const pageSuffix = page ? `, page ${page}` : '';
  const referenceSuffix = reference ? ` for ${reference}` : '';

  return `${label}${pageSuffix} - council planning preview${referenceSuffix}.`;
}

function buildAltText(document: PlanningDocument, application: PlanningApplication, index: number) {
  const reference = clean(application.reference_no);
  const address = clean(application.address);
  const title = getDocumentTitle(document, index);
  const page = clean(document.page_number);
  return [
    `Planning document preview for application ${reference || 'reference unavailable'}`,
    address ? `at ${address}` : '',
    title ? `showing ${title}` : '',
    page ? `page ${page}` : '',
  ]
    .filter(Boolean)
    .join(', ');
}

export function getPlanningArticlePhotoItems(
  application?: PlanningApplication | null,
  limit = 3
): PlanningArticlePhotoItem[] {
  const documents = Array.isArray(application?.documents)
    ? application.documents.filter((document): document is PlanningDocument => Boolean(clean(document?.thumbnail_url)))
    : [];

  if (!documents.length) return [];

  const selectedDocuments = isPanApplication(application)
    ? [documents.find(isLocationPlan) || documents[0]]
    : documents.slice(0, limit);

  return selectedDocuments.map((document, index) => ({
    document,
    index,
    href: getDocumentHref(document),
    title: getDocumentTitle(document, index),
    caption: buildCaption(document, application || {}, index),
    alt: buildAltText(document, application || {}, index),
    thumbnailUrl: clean(document.thumbnail_url),
  }));
}

export function PlanningArticleInlinePhoto({
  item,
  priority = false,
  className = '',
}: {
  item: PlanningArticlePhotoItem;
  priority?: boolean;
  className?: string;
}) {
  const image = (
    <img
      src={item.thumbnailUrl}
      alt={item.alt}
      loading={priority ? 'eager' : 'lazy'}
      className="aspect-[4/3] w-full bg-[var(--c-bg-secondary)] object-cover"
    />
  );

  return (
    <figure className={`article-photo-section overflow-hidden rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] ${className}`}>
      {item.href ? (
        <a
          href={item.href}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="group block focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--c-accent)]"
          aria-label={`Open official planning document: ${item.title}`}
        >
          {image}
        </a>
      ) : (
        image
      )}
      <figcaption className="flex items-start justify-between gap-3 border-t border-[var(--c-border)] px-4 py-3 text-xs leading-relaxed text-[var(--c-text-secondary)]">
        <span>{item.caption}</span>
        {item.href ? <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--c-accent)]" aria-hidden="true" /> : null}
      </figcaption>
    </figure>
  );
}

export default function PlanningArticlePhotos({ application }: { application?: PlanningApplication | null }) {
  const photos = getPlanningArticlePhotoItems(application);

  if (!photos.length) return null;

  return (
    <>
      {photos.map((item, index) => (
        <PlanningArticleInlinePhoto
          key={`${item.thumbnailUrl}-${index}`}
          item={item}
          priority={index === 0}
          className="my-8"
        />
      ))}
    </>
  );
}
